import type { CategoryRead } from '$lib/api/generated';
import type { CategoryTree } from '$lib/api/categories';
import type { DropIntent } from './CategoryTreePanel.types';

/**
 * Compute drop intent based on mouse Y position relative to target element.
 * Divides the element into 3 zones: top 25% = 'before', middle 50% = 'inside', bottom 25% = 'after'.
 */
export function computeDropIntent(
	mouseY: number,
	rect: { top: number; height: number }
): DropIntent {
	const relativeY = mouseY - rect.top;
	const ratio = relativeY / rect.height;

	if (ratio < 0.25) return 'before';
	if (ratio > 0.75) return 'after';
	return 'inside';
}

/**
 * Collect all descendant IDs (including ancestorId itself) using the children map.
 * Returns a Set for O(1) lookup — designed for hot render paths (dragover, snippet rendering).
 */
export function getDescendantIds(
	ancestorId: string,
	childrenMap: Map<string | null, { id: string }[]>
): Set<string> {
	const descendants = new Set<string>();
	descendants.add(ancestorId);

	const stack = [ancestorId];
	while (stack.length > 0) {
		const id = stack.pop()!;
		const children = childrenMap.get(id);
		if (children) {
			for (const child of children) {
				if (!descendants.has(child.id)) {
					descendants.add(child.id);
					stack.push(child.id);
				}
			}
		}
	}

	return descendants;
}

/**
 * Build new sibling order after a drag operation.
 * Returns ordered IDs for siblings at the same level.
 */
export function buildReorderedSiblings(
	currentOrder: string[],
	draggedId: string,
	targetId: string,
	intent: 'before' | 'after'
): string[] {
	const filtered = currentOrder.filter((id) => id !== draggedId);

	const targetIndex = filtered.indexOf(targetId);
	if (targetIndex === -1) return currentOrder;

	const insertIndex = intent === 'before' ? targetIndex : targetIndex + 1;
	filtered.splice(insertIndex, 0, draggedId);

	return filtered;
}

/**
 * Extract display order from a CategoryTree (DFS flatten).
 * Returns a flat array of category IDs in tree traversal order.
 */
export function extractDisplayOrder(tree: CategoryTree[]): string[] {
	const result: string[] = [];

	function traverse(nodes: CategoryTree[]) {
		for (const node of nodes) {
			result.push(node.id);
			if (node.children?.length) {
				traverse(node.children);
			}
		}
	}

	traverse(tree);
	return result;
}

/**
 * Clone a children map with a category moved between parents.
 * Returns a new map reflecting the move — use before rebuildFullDisplayOrder
 * to get correct DFS when the reactive data is stale after an API call.
 */
export function applyMoveToChildrenMap(
	childrenMap: Map<string | null, CategoryRead[]>,
	movedCategory: CategoryRead,
	fromParentId: string | null,
	toParentId: string | null
): Map<string | null, CategoryRead[]> {
	const result = new Map<string | null, CategoryRead[]>();

	for (const [parentId, children] of childrenMap) {
		result.set(parentId, [...children]);
	}

	// Remove from old parent
	const oldSiblings = result.get(fromParentId);
	if (oldSiblings) {
		result.set(
			fromParentId,
			oldSiblings.filter((c) => c.id !== movedCategory.id)
		);
	}

	// Add to new parent (at end — caller reorders via rebuildFullDisplayOrder)
	const newSiblings = [...(result.get(toParentId) || [])];
	if (!newSiblings.some((c) => c.id === movedCategory.id)) {
		newSiblings.push(movedCategory);
	}
	result.set(toParentId, newSiblings);

	return result;
}

/**
 * Rebuild the full display order (DFS) with one parent's children reordered.
 * Takes the current children map and overrides children order for a specific parent.
 * Returns a flat array of ALL category IDs in the new DFS order.
 * Children not in newSiblingOrder are appended at the end (handles concurrent additions).
 */
export function rebuildFullDisplayOrder(
	childrenMap: Map<string | null, { id: string }[]>,
	siblingParentId: string | null,
	newSiblingOrder: string[]
): string[] {
	const result: string[] = [];

	function traverse(parentId: string | null) {
		let children = childrenMap.get(parentId) || [];

		if (parentId === siblingParentId) {
			const childMap = new Map(children.map((c) => [c.id, c]));
			const orderedIds = new Set(newSiblingOrder);
			children = [
				...newSiblingOrder.filter((id) => childMap.has(id)).map((id) => childMap.get(id)!),
				...children.filter((c) => !orderedIds.has(c.id))
			];
		}

		for (const child of children) {
			result.push(child.id);
			traverse(child.id);
		}
	}

	traverse(null);
	return result;
}
